import React, { useState, useEffect } from "react";
import { Box, Typography, FormControlLabel, Checkbox, TextField, Button, Divider } from "@mui/material";
import axios from "axios";
import useDialog from "../../../hooks/useDialog";

interface UniversalGrantFieldsProps {
  applicationInformation: any;
  applicationId: string;
  agencyLevel: number;
  agencyDepartment: any;
}

interface ReimbursementData {
  status: boolean;
  report_number: string;
  report_date: string; // ISO date string (YYYY-MM-DD)
  reimbursed_amount: number;
}

interface GrantInformation {
  partially_reimbursed?: ReimbursementData;
  fully_reimbursed?: ReimbursementData;
  beneficiary_limit_fully_used?: boolean;
  grant_fully_used_on_original_loan?: boolean;
  comment?: string;
}

const UniversalGrantFields: React.FC<UniversalGrantFieldsProps> = ({ applicationInformation, applicationId, agencyLevel, agencyDepartment }) => {
  const { promptConfirm, showSnackbar } = useDialog();
  const [grantInformation, setGrantInformation] = useState<GrantInformation>({});
  const [showPartial, setShowPartial] = useState<boolean>(false);
  const [showFull, setShowFull] = useState<boolean>(false);
  const [totalReimbursedAmount, setTotalReimbursedAmount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateForInput = (isoDateString: string): string => {
    if (!isoDateString) return "";
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Fetch existing data when component mounts
  useEffect(() => {
    const fetchGrantData = async () => {
      try {
        const data = applicationInformation.grant_information || {};

        const formattedData: GrantInformation = {
          comment: data.comment || "",
          beneficiary_limit_fully_used: data.beneficiary_limit_fully_used || false,
          grant_fully_used_on_original_loan: data.grant_fully_used_on_original_loan || false,
        };

        if (data.partially_reimbursed) {
          formattedData.partially_reimbursed = {
            ...data.partially_reimbursed,
            report_date: formatDateForInput(data.partially_reimbursed.report_date),
          };
        }

        if (data.fully_reimbursed) {
          formattedData.fully_reimbursed = {
            ...data.fully_reimbursed,
            report_date: formatDateForInput(data.fully_reimbursed.report_date),
          };
        }

        setGrantInformation(formattedData);

        setShowPartial(!!data.partially_reimbursed?.status);
        setShowFull(!!data.fully_reimbursed?.status);

        // Calculate total reimbursed amount
        let totalAmount = 0;
        if (data.partially_reimbursed?.status) {
          totalAmount += data.partially_reimbursed.reimbursed_amount || 0;
        }
        if (data.fully_reimbursed?.status) {
          totalAmount += data.fully_reimbursed.reimbursed_amount || 0;
        }
        setTotalReimbursedAmount(totalAmount);
      } catch (error) {
        console.error("Error fetching grant data:", error);
      }
    };

    fetchGrantData();
  }, [applicationInformation]);

  // Handle checkbox change
  const handleCheckboxChange = (type: "partial" | "full") => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (type === "partial") {
      setShowPartial(checked);
      if (!checked) {
        // Remove partially_reimbursed data
        setGrantInformation((prev) => {
          const updated = { ...prev };
          delete updated.partially_reimbursed;
          return updated;
        });
      } else {
        // Initialize partially_reimbursed data if not present
        setGrantInformation((prev) => ({
          ...prev,
          partially_reimbursed: {
            status: true,
            report_number: "",
            report_date: "",
            reimbursed_amount: 0,
          },
        }));
      }
    } else if (type === "full") {
      setShowFull(checked);
      if (!checked) {
        // Remove fully_reimbursed data
        setGrantInformation((prev) => {
          const updated = { ...prev };
          delete updated.fully_reimbursed;
          return updated;
        });
      } else {
        // Initialize fully_reimbursed data if not present
        setGrantInformation((prev) => ({
          ...prev,
          fully_reimbursed: {
            status: true,
            report_number: "",
            report_date: "",
            reimbursed_amount: 0,
          },
        }));
      }
    }
  };

  // Handle changes in form fields
  const handleChange = (type: "partially_reimbursed" | "fully_reimbursed", field: keyof ReimbursementData, value: any) => {
    setGrantInformation((prevData) => ({
      ...prevData,
      [type]: {
        ...prevData[type],
        [field]: value,
      },
    }));
  };

  // Recalculate total reimbursed amount when grantInformation changes
  useEffect(() => {
    let totalAmount = 0;
    if (grantInformation.partially_reimbursed?.status) {
      totalAmount += grantInformation.partially_reimbursed.reimbursed_amount || 0;
    }
    if (grantInformation.fully_reimbursed?.status) {
      totalAmount += grantInformation.fully_reimbursed.reimbursed_amount || 0;
    }
    setTotalReimbursedAmount(totalAmount);
  }, [grantInformation]);

  // Handle form submission
  const handleSubmit = async () => {
    // Ask for confirmation before submitting
    const confirmed = await promptConfirm({
      title: "დადასტურება",
      message: "დარწმუნებული ხართ, რომ გსურთ გრანტის ინფორმაციის განახლება?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`/agency/universal/update_grant/${applicationId}`, { grant_information: grantInformation });
      showSnackbar("გრანტის ინფორმაცია წარმატებით განახლდა", "success");
      
      // Reload the page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error("Error updating grant data:", error);
      const errorMessage = error?.response?.data?.message || "გრანტის ინფორმაციის განახლებისას დაფიქსირდა შეცდომა";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const canEditGrant = agencyDepartment?.title === "რისკების დეპარტამენტი" && agencyLevel >= 1;

  if (!canEditGrant) {
    return (
      <Box sx={{ maxWidth: 600, margin: "0 auto" }}>
        {applicationInformation?.grant_information?.partially_reimbursed?.status && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
              ნაწილობრივ ანაზღაურებული
            </Typography>
            <Typography variant="body2">მოხსენებითი ბარათის ნომერი: {applicationInformation.grant_information.partially_reimbursed.report_number}</Typography>
            <Typography variant="body2">
              მოხსენებითი ბარათის გაშვების თარიღი: {applicationInformation.grant_information.partially_reimbursed.report_date}
            </Typography>
            <Typography variant="body2">
              ანაზღაურებული გრანტის თანხა: ₾ {applicationInformation.grant_information.partially_reimbursed.reimbursed_amount?.toLocaleString()}
            </Typography>
          </Box>
        )}
        {applicationInformation?.grant_information?.fully_reimbursed?.status && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
              სრულად ანაზღაურებული
            </Typography>
            <Typography variant="body2">მოხსენებითი ბარათის ნომერი: {applicationInformation.grant_information.fully_reimbursed.report_number}</Typography>
            <Typography variant="body2">
              მოხსენებითი ბარათის გაშვების თარიღი: {applicationInformation.grant_information.fully_reimbursed.report_date}
            </Typography>
            <Typography variant="body2">
              ანაზღაურებული გრანტის თანხა: ₾ {applicationInformation.grant_information.fully_reimbursed.reimbursed_amount?.toLocaleString()}
            </Typography>
          </Box>
        )}
        
        {/* Display new status fields */}
        {(applicationInformation?.grant_information?.beneficiary_limit_fully_used || applicationInformation?.grant_information?.grant_fully_used_on_original_loan) && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
              გრანტის სტატუსი
            </Typography>
            {applicationInformation.grant_information.beneficiary_limit_fully_used && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                ბენეფიციარის მიერ ათვისებულია ლიმიტი სრულად (≥500,000₾)
              </Typography>
            )}
            {applicationInformation.grant_information.grant_fully_used_on_original_loan && (
              <Typography variant="body2">
                გრანტი სრულად ათვისებულია საწყის სესხზე
              </Typography>
            )}
          </Box>
        )}
        
        {applicationInformation?.grant_information?.comment && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              კომენტარი:
            </Typography>
            <Typography variant="body2">{applicationInformation.grant_information.comment}</Typography>
          </Box>
        )}
        {!applicationInformation?.grant_information?.partially_reimbursed?.status && !applicationInformation?.grant_information?.fully_reimbursed?.status && (
          <Typography variant="body2">გრანტის ანაზღაურების ინფორმაცია არ არის ხელმისაწვდომი</Typography>
        )}
        {totalReimbursedAmount > 0 && (
          <Typography variant="body1" sx={{ mt: 2, fontWeight: "bold", fontSize: "1.1rem" }}>
            სულ ანაზღაურებული თანხა: ₾ {formatNumber(totalReimbursedAmount)}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, margin: "0 auto" }}>
      {/* Checkboxes */}
      <FormControlLabel control={<Checkbox checked={showPartial} onChange={handleCheckboxChange("partial")} />} label="ნაწილობრივ ანაზღაურებული" />
      <FormControlLabel control={<Checkbox checked={showFull} onChange={handleCheckboxChange("full")} />} label="სრულად ანაზღაურებული" />

      <Divider sx={{ my: 2 }} />

      {/* New Checkboxes */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
          გრანტის სტატუსი
        </Typography>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={grantInformation.beneficiary_limit_fully_used || false}
              onChange={(e) =>
                setGrantInformation((prev) => ({
                  ...prev,
                  beneficiary_limit_fully_used: e.target.checked,
                }))
              }
            />
          }
          label="ბენეფიციარის მიერ ათვისებულია ლიმიტი სრულად (≥500,000₾)"
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={grantInformation.grant_fully_used_on_original_loan || false}
              disabled={true}
              sx={{ 
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.6)',
                }
              }}
            />
          }
          label="გრანტი სრულად ათვისებულია საწყის სესხზე"
        />
        
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Partially Reimbursed Fields */}
      {showPartial && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            ნაწილობრივ ანაზღაურებული
          </Typography>

          {/* Field I: Report Number */}
          <TextField
            label="მოხსენებითი ბარათის ნომერი"
            variant="outlined"
            fullWidth
            value={grantInformation.partially_reimbursed?.report_number || ""}
            onChange={(e) => handleChange("partially_reimbursed", "report_number", e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Field II: Report Date */}
          <TextField
            label="მოხსენებითი ბარათის გაშვების თარიღი"
            variant="outlined"
            fullWidth
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={grantInformation.partially_reimbursed?.report_date || ""}
            onChange={(e) => handleChange("partially_reimbursed", "report_date", e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Field III: Reimbursed Amount */}
          <TextField
            label="ანაზღაურებული გრანტის თანხა - ₾"
            variant="outlined"
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            value={grantInformation.partially_reimbursed?.reimbursed_amount || ""}
            onChange={(e) => handleChange("partially_reimbursed", "reimbursed_amount", parseFloat(e.target.value) || 0)}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      {/* Fully Reimbursed Fields */}
      {showFull && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            სრულად ანაზღაურებული
          </Typography>

          {/* Field I: Report Number */}
          <TextField
            label="მოხსენებითი ბარათის ნომერი"
            variant="outlined"
            fullWidth
            value={grantInformation.fully_reimbursed?.report_number || ""}
            onChange={(e) => handleChange("fully_reimbursed", "report_number", e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Field II: Report Date */}
          <TextField
            label="მოხსენებითი ბარათის გაშვების თარიღი"
            variant="outlined"
            fullWidth
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={grantInformation.fully_reimbursed?.report_date || ""}
            onChange={(e) => handleChange("fully_reimbursed", "report_date", e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Field III: Reimbursed Amount */}
          <TextField
            label="ანაზღაურებული გრანტის თანხა - ₾"
            variant="outlined"
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            value={grantInformation.fully_reimbursed?.reimbursed_amount || ""}
            onChange={(e) => handleChange("fully_reimbursed", "reimbursed_amount", parseFloat(e.target.value) || 0)}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      {/* Comment Field - Single field for all grant information */}
      {(showPartial || showFull) && (
        <Box sx={{ mb: 2 }}>
          <TextField
            label="კომენტარი"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            value={grantInformation.comment || ""}
            onChange={(e) => setGrantInformation((prev) => ({ ...prev, comment: e.target.value }))}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      {/* Total Reimbursed Amount */}
      <Typography variant="body1" sx={{ mb: 2, fontWeight: "bold", fontSize: "1.1rem" }}>
        სულ ანაზღაურებული თანხა: ₾ {formatNumber(totalReimbursedAmount)}
      </Typography>

      {/* Submit Button */}
      <Button disabled={loading} variant="contained" color="primary" onClick={handleSubmit} fullWidth>
        {loading ? "იტვირთება..." : "განახლება"}
      </Button>
    </Box>
  );
};

export default UniversalGrantFields;
